import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { extNav, extSite } from '../ext';
import { useAuth, signOut } from '../AuthContext';
import { useJobs, jobIsActive } from '../data/firestore';
import { useInternalClaim } from '../pages/DevConsole';
import ChangePassword from './ChangePassword';
import { TEMPLATE_ACTIVE } from '../templates/Site';
import { APP_NAME } from '../data/kitEnv';
import { useTenant, isPrivileged } from '../data/entities';
import { mailStatus } from '../data/mailClient';

// Mirrors App.js: a starter template or a layer extSite owns '/'.
const SITE_OWNS_HOME = TEMPLATE_ACTIVE || !!extSite;

// Workspace switcher: "All" (org-wide + every seat) or one workspace. Hidden
// until the org has a workspace the viewer can see.
function WorkspaceSwitch() {
  const t = useTenant();
  if (!t.orgId || !t.workspaces?.length) return null;
  return (
    <select
      className="cs-input cs-select cs-nav-ws"
      value={t.workspace || ''}
      onChange={(e) => t.setWorkspace(e.target.value || null)}
      aria-label="Workspace"
      title={`Workspace · you are ${t.wsRole || t.role || 'a guest'} here`}
    >
      <option value="">All workspaces</option>
      {t.workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
    </select>
  );
}

export default function Navbar() {
  const { user } = useAuth();
  const tenant = useTenant();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const location = useLocation();
  const jobs = useJobs(100, !!user);
  const activeJobs = (jobs || []).filter(jobIsActive).length;
  // Dev Console entry is only shown to accounts with the internal claim.
  const internal = useInternalClaim();
  // Mail entry appears once the email service is on for this instance.
  const [mailOn, setMailOn] = useState(false);
  const [mailUnread, setMailUnread] = useState(0);
  useEffect(() => {
    if (!user) { setMailOn(false); setMailUnread(0); return undefined; }
    let alive = true;
    const tick = () => mailStatus({ unread: true }).then((s) => { if (!alive) return; setMailOn(!!s?.enabled); setMailUnread(Number(s?.unread) || 0); }).catch(() => {});
    tick();
    const id = setInterval(tick, 120000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Collapse the mobile menu whenever we navigate to a new route.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const close = () => setOpen(false);

  return (
    <nav className={`cs-nav ${scrolled || open ? 'scrolled' : ''} ${open ? 'open' : ''}`}>
      <Link to={SITE_OWNS_HOME ? '/app' : '/'} className="cs-brand" onClick={close}>
        {APP_NAME}
      </Link>
      <button
        type="button"
        className="cs-nav-burger"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
      >
        {open ? '✕' : '☰'}
      </button>
      <div className="cs-nav-links">
        {user && (
          <NavLink to={SITE_OWNS_HOME ? '/app' : '/'} end onClick={close}>
            Items
          </NavLink>
        )}
        {user && (
          <NavLink to="/jobs" className="cs-nav-jobs" onClick={close} title="Background jobs: processing, fan-outs, pipeline runs">
            Jobs
            {activeJobs > 0 && <span className="cs-nav-jobs-badge">{activeJobs}</span>}
          </NavLink>
        )}
        {user && (
          <NavLink to="/pipelines" onClick={close}>
            Pipelines
          </NavLink>
        )}
        {user && (
          <NavLink to="/review" onClick={close} title="Human review queue for gated pipeline steps">
            Review
          </NavLink>
        )}
        {user && (
          <NavLink to="/agents" onClick={close}>
            Agents
          </NavLink>
        )}
        {user && mailOn && (
          <NavLink to="/mail" className="cs-nav-jobs" onClick={close} title="Your inbox on the app's domain">
            Mail
            {mailUnread > 0 && <span className="cs-nav-jobs-badge">{mailUnread > 99 ? '99+' : mailUnread}</span>}
          </NavLink>
        )}
        {user && isPrivileged(tenant.role) && (
          <NavLink to="/team" onClick={close} title="Organization members, workspaces and seats">
            Team
          </NavLink>
        )}
        {user && SITE_OWNS_HOME && (
          <NavLink to="/?site=1" onClick={close} title="Your public site">
            View site
          </NavLink>
        )}
        {/* Layer nav links (frontend/src/ext/ is layer-owned) */}
        {user && extNav.map((l) => (
          <NavLink key={l.to} to={l.to} onClick={close}>
            {l.label}
          </NavLink>
        ))}
        {internal && (
          <NavLink to="/dev" onClick={close} title="Dev Console: agent sessions, deploys, ops">
            Dev Console
          </NavLink>
        )}
      </div>
      <div className="cs-nav-spacer" />
      {user ? (
        <div className="cs-nav-user">
          <WorkspaceSwitch />
          <span className="cs-nav-email">{user.email}</span>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => setPwOpen(true)}
            title="Change password"
          >
            Password
          </button>
          <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => signOut()}>
            Sign out
          </button>
        </div>
      ) : (
        <div className="cs-nav-user">
          <Link to="/login" className="cs-btn cs-btn-outline cs-btn-sm" onClick={close}>
            Sign in
          </Link>
        </div>
      )}
      {pwOpen && <ChangePassword onClose={() => setPwOpen(false)} />}
    </nav>
  );
}
